import React, { useMemo, useState } from 'react';
import { LockKeyhole, X } from 'lucide-react';
import {
  addLockedPhrase,
  answerWords,
  phraseFromSelection,
  updateWordSelection,
} from '../../utils/reflectFlow';

const LOCK_TOGGLE_CLASSES = [
  'inline-flex min-h-11 items-center gap-2 rounded-full border border-cyan/40 px-4',
  'text-base font-black text-cyan transition hover:bg-cyan/10',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan',
  'disabled:cursor-not-allowed disabled:border-white/10 disabled:text-white/35',
].join(' ');

const LOCKED_PHRASE_CLASSES = [
  'inline-flex min-h-11 items-center gap-2 rounded-full border border-cyan/50',
  'bg-cyan/10 px-4 text-base font-extrabold text-cyan',
  'shadow-[0_0_16px_rgba(0,255,221,0.16)]',
].join(' ');

const WORD_BUTTON_CLASSES = [
  'min-h-11 rounded-xl border px-3 text-base font-bold transition',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan',
].join(' ');

const ADD_PHRASE_CLASSES = [
  'mt-4 min-h-11 rounded-full bg-cyan px-5 text-base font-black text-black transition',
  'hover:bg-cyan/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
  'disabled:opacity-35',
].join(' ');

const LockWords = ({ text, phrases, totalLocks, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState([]);
  const words = useMemo(() => answerWords(text), [text]);
  const selectedPhrase = phraseFromSelection(text, selection);
  const atPhraseLimit = totalLocks >= 5;

  const addPhrase = () => {
    const next = addLockedPhrase(phrases, selectedPhrase, Math.max(0, phrases.length + (5 - totalLocks)));
    if (next !== phrases && next.length > phrases.length) onChange(next);
    setSelection([]);
  };

  if (!String(text || '').trim()) return null;

  return (
    <section className="mt-5 rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          disabled={disabled || atPhraseLimit}
          className={LOCK_TOGGLE_CLASSES}
        >
          <LockKeyhole className="h-4 w-4" />
          {open ? 'Close word picker' : 'Lock words'}
        </button>
        <p className="text-base font-bold text-white/55">{totalLocks} of 5 phrases locked</p>
      </div>

      {disabled && (
        <p className="mt-3 text-base text-white/55">Private answers cannot have locked words.</p>
      )}

      {phrases.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" aria-label="Locked phrases">
          {phrases.map((phrase) => (
            <span key={phrase} className={LOCKED_PHRASE_CLASSES}>
              {phrase}
              <button
                type="button"
                onClick={() => onChange(phrases.filter((item) => item !== phrase))}
                className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
                aria-label={`Remove locked phrase ${phrase}`}
              >
                <X className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && !disabled && !atPhraseLimit && (
        <div className="mt-5 border-t border-white/10 pt-4">
          <p className="mb-3 text-base leading-relaxed text-white/65">
            Tap words next to each other. Choose up to 8 words for one phrase.
          </p>
          <div className="flex flex-wrap gap-2">
            {words.map((word) => {
              const active = selection.includes(word.index);
              return (
                <button
                  key={`${word.start}-${word.text}`}
                  type="button"
                  onClick={() => setSelection((current) => updateWordSelection(current, word.index))}
                  className={`${WORD_BUTTON_CLASSES} ${active
                    ? 'border-cyan bg-cyan text-black shadow-[0_0_14px_rgba(0,255,221,0.35)]'
                    : 'border-white/15 bg-white/[0.05] text-white hover:border-cyan/50'
                  }`}
                >
                  {word.text}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addPhrase}
            disabled={!selectedPhrase}
            className={ADD_PHRASE_CLASSES}
          >
            Lock this phrase
          </button>
        </div>
      )}
    </section>
  );
};

export default LockWords;
